import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLegal } from '../lib/legalStrings';

/**
 * Renders any of the two legal pages (Privacy, Terms) from a language-aware
 * section object. Keeping one component covers both since the structure is identical.
 */
export default function LegalPage({ kind }) {
  const { language } = useLanguage();
  const L = useLegal(language);
  const content = kind === 'privacy' ? L.privacy : L.terms;

  return (
    <div className="min-h-screen bg-background" data-testid={`legal-page-${kind}`}>
      <div className="noise-overlay" />

      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-back-home">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{L.common.backHome}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-sm">AIgnyx</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <p className="text-xs text-muted-foreground mb-2">{L.lastUpdated}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            {content.title}
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-10">
            {content.intro}
          </p>

          <div className="space-y-8">
            {content.sections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-lg font-semibold mb-3 pb-2 border-b border-border">
                  {section.heading}
                </h2>
                <ul className="space-y-2 text-sm leading-relaxed">
                  {section.items.map((item, j) => (
                    <li key={j} className="pl-4 relative">
                      <span
                        className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-primary"
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-16 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <span>© 2026 AIgnyx</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-primary">{L.common.privacy}</Link>
              <Link to="/terms" className="hover:text-primary">{L.common.terms}</Link>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
